import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useCallback } from 'react';
import { VEHICLE_DETAILS_ROUTING_UPDATES_FLAG } from '~/common/models/featureFlags.model';
import {
  type BuildAssetUrlFn,
  buildAssetUrl,
} from '~/common/utils/asset-url.utils';
import { useOrgId } from './useOrgId';

export {
  type BuildAssetUrlFn,
  buildAssetUrl,
} from '~/common/utils/asset-url.utils';

/**
 * Hook that returns a function to build asset URLs.
 *
 * Uses organization-based routing if both the feature flag is enabled
 * and an organization ID is available, otherwise falls back to legacy routing.
 *
 * @example
 * ```tsx
 * const { buildAssetUrl } = useBuildAssetUrl();
 * const vehicleLink = buildAssetUrl(vehicleId);
 * ```
 *
 * @returns Object containing the buildAssetUrl function and context values
 */
export const useBuildAssetUrl = () => {
  const organizationsId = useOrgId();
  const { getFlag } = useFeatureFlags();
  const isVehicleDetailsRoutingUpdatesEnabled = Boolean(
    getFlag(VEHICLE_DETAILS_ROUTING_UPDATES_FLAG)
  );

  const buildAssetUrlFn: BuildAssetUrlFn = useCallback(
    (assetId: string) => {
      return buildAssetUrl(
        assetId,
        organizationsId,
        isVehicleDetailsRoutingUpdatesEnabled
      );
    },
    [organizationsId, isVehicleDetailsRoutingUpdatesEnabled]
  );

  return {
    buildAssetUrl: buildAssetUrlFn,
    organizationsId,
    isVehicleDetailsRoutingUpdatesEnabled,
  };
};
